// Part 2 (Lane D): what a player or the GM reads when getting back into a
// game fails, and what they can do next. Pure — no Supabase, no React — so
// every row of the spec's copy map (SPEC §4) is unit-tested on its own.
//
// Game 8827: Worsie's phone lost its claim token, every rejoin was refused
// with 42501, and the join screen said "Couldn't reach the game — check your
// connection". The table blamed the wifi all evening. The rule here is that a
// refusal is never described as a network problem: only an error that
// actually looks like a dropped request gets the connection copy.
//
// Each helper returns { kind, message, actions }. `actions` is an ordered list
// of the next steps the screen should offer, primary first.

export const ACTIONS = Object.freeze({
  ASK_HOST: 'ask_host',
  SIGN_IN: 'sign_in',
  HOST_CODE: 'host_code',
  WHATSAPP: 'whatsapp',
  EDIT_NAME: 'edit_name',
  RETRY: 'retry',
  ASK_AGAIN: 'ask_again',
  RETYPE: 'retype',
  GIVE_CODE: 'give_code',
  MARK_AWAY: 'mark_away',
  ASK_TO_JOIN: 'ask_to_join',
});

const A = ACTIONS;

const nameOr = (name, fallback = 'that player') => (name && String(name).trim()) || fallback;

export const COPY = Object.freeze({
  ownershipStarted: (name) => `That's you, but this phone isn't linked to ${nameOr(name, 'that player')} any more.`,
  ownershipLobby: (name) => `${nameOr(name, 'That player')} is already playing on another phone. Pick a different name.`,
  sessionEnded: 'This game has already finished — ask your GM for a new code.',
  rejoinRateLimited: 'Too many attempts too fast — wait before trying again; it can take up to an hour to clear.',
  readmitRateLimited: (name) => `Lots of requests for ${nameOr(name)} right now. Ask the GM to give you a code from their phone.`,
  startedNewName: 'This game has started. If you were playing, type the exact nickname you used.',
  featureOff: "Getting back in this way isn't switched on for this game. Message us and we'll help.",
  nameTaken: 'That name is already taken in this game — try another.',
  noSuchPlayer: (name) => `There's no player called ${nameOr(name, 'that')} in this game. It has to match exactly.`,
  requestExpired: 'Your request timed out. Ask again when the GM is ready.',
  declined: "The GM didn't let this phone in. If that's a mistake, ask them to try again.",
  wrongCode: "That code doesn't match. Check it with the GM.",
  locked: (s) => `Too many wrong codes from this connection. Try again in ${Math.max(1, Math.ceil(Number(s) || 60))} seconds.`,
  codeExpired: 'That code has expired. Ask the GM for a new one.',
  gmWrongCode: (triesLeft) => {
    const n = Number(triesLeft);
    const tail = Number.isFinite(n) ? ` ${n} ${n === 1 ? 'try' : 'tries'} left.` : '';
    return `That code doesn't match — check their screen.${tail}`;
  },
  gmTooManyWrong: (name) => `Too many wrong codes, so this request was closed. Ask ${nameOr(name)} to ask again, or give them a code.`,
  gmStillActive: (name) => {
    const n = nameOr(name);
    return `${n}'s other phone still counts as in the game. If it's lost or broken, this phone can take the seat instead.`;
  },
  gmRequestGone: 'That request has expired.',
  gmOnly: 'Only the game master can do that.',
  gmNotStarted: 'This only works once the game has started.',
  markedAway: 'You were marked away. Ask the GM to let you back in.',
  network: "Couldn't reach the game — check your connection and try again.",
  unknown: "That didn't work. Try again, or message us on WhatsApp.",
  codeFormatHost: "That's not 8 digits. Check the code on the GM's phone.",
  codeFormatGm: "That's not 4 characters. Check the code on their phone.",
  // Late joiners (migration 233, audit E1).
  lateJoinStarted: 'This game has already started. Ask the GM to let you join — you’ll score from when you join.',
  lateJoinRateLimited: 'Lots of people are asking to join right now. Try again in a few minutes.',
  lateJoinNotStarted: 'The game hasn’t started yet, so you can join straight away.',
  gmNameTaken: 'Someone in the game already has that name. Ask them to pick another name and ask again.',
});

/** The Postgres / PostgREST error code, whatever shape the error arrived in. */
export function errorCode(err) {
  if (!err || typeof err !== 'object') return '';
  return String(err.code ?? '').trim();
}

const messageOf = (err) => String((err && (err.message || err.error_description)) || '');

/**
 * A request that never got an answer: offline, a stalled socket that our own
 * timeout cut off, a fetch that threw. A PostgREST refusal always carries a
 * code, so an error WITH a Postgres code is never a network problem.
 */
export function isNetworkError(err) {
  if (!err) return false;
  const code = errorCode(err);
  if (code && !/^(0|ECONN|ETIMEDOUT|ENOTFOUND|ERR_NETWORK)/i.test(code)) return false;
  const msg = messageOf(err);
  if (/timed out|failed to fetch|networkerror|network request failed|load failed|fetch failed|network|offline|aborted/i.test(msg)) {
    return true;
  }
  // No code and no message at all: a bare rejection from a dead socket.
  return !code && !msg;
}

/** 42501 raised by rejoin_session_secure because this phone can't prove it owns the seat. */
export function isOwnershipRefusal(err) {
  return /original device|sign in to the account/i.test(messageOf(err));
}

/** Migration 199 raises "session has ended" under 42501 too. */
export function isSessionEnded(err) {
  return /session has ended/i.test(messageOf(err));
}

const result = (kind, message, actions = []) => ({ kind, message, actions });

/**
 * rejoin_session_secure (or join_game_session) failed.
 * `started` — the game is under way, so a readmit is possible.
 * `readmit` — room_readmit is effective for this session.
 */
export function rejoinFailure(err, { name, started = false, readmit = false } = {}) {
  const code = errorCode(err);
  if (code === 'P0429' || /too many rejoins/i.test(messageOf(err))) {
    return result('rate_limited', COPY.rejoinRateLimited, []);
  }
  if (isSessionEnded(err) || code === 'P0403') {
    return result('ended', COPY.sessionEnded, []);
  }
  if (isOwnershipRefusal(err) || (code === '42501' && !isSessionEnded(err))) {
    if (!started) {
      return result('ownership_lobby', COPY.ownershipLobby(name), readmit ? [A.ASK_HOST, A.EDIT_NAME] : [A.EDIT_NAME]);
    }
    return result(
      'ownership_started',
      COPY.ownershipStarted(name),
      readmit ? [A.ASK_HOST, A.HOST_CODE, A.WHATSAPP] : [A.SIGN_IN, A.WHATSAPP]
    );
  }
  if (code === 'P0409' || /already started/i.test(messageOf(err))) {
    return result('started_new_name', COPY.startedNewName, [A.EDIT_NAME]);
  }
  if (code === '23505') return result('name_taken', COPY.nameTaken, [A.EDIT_NAME]);
  if (code === 'P0501') return result('feature_off', COPY.featureOff, [A.SIGN_IN, A.WHATSAPP]);
  if (isNetworkError(err)) return result('network', COPY.network, [A.RETRY]);
  return result('unknown', COPY.unknown, [A.RETRY, A.WHATSAPP]);
}

/** request_readmit raised. */
export function readmitRequestFailure(err, { name } = {}) {
  const code = errorCode(err);
  if (code === 'P0429') return result('rate_limited', COPY.readmitRateLimited(name), [A.HOST_CODE, A.WHATSAPP]);
  if (code === 'P0404') return result('no_such_player', COPY.noSuchPlayer(name), [A.EDIT_NAME]);
  if (code === 'P0403') return result('ended', COPY.sessionEnded, []);
  if (code === 'P0501') return result('feature_off', COPY.featureOff, [A.SIGN_IN, A.WHATSAPP]);
  if (code === 'P0410') return result('expired', COPY.requestExpired, [A.ASK_AGAIN]);
  if (isNetworkError(err)) return result('network', COPY.network, [A.RETRY]);
  return result('unknown', COPY.unknown, [A.RETRY, A.WHATSAPP]);
}

/** request_late_join raised (migration 233). */
export function lateJoinRequestFailure(err) {
  const code = errorCode(err);
  if (code === 'P0429') return result('rate_limited', COPY.lateJoinRateLimited, [A.WHATSAPP]);
  if (code === '23505') return result('name_taken', COPY.nameTaken, [A.EDIT_NAME]);
  if (code === 'P0422') return result('not_started', COPY.lateJoinNotStarted, [A.RETRY]);
  if (code === 'P0403') return result('ended', COPY.sessionEnded, []);
  if (code === 'P0404') return result('no_game', COPY.sessionEnded, []);
  if (code === 'P0501') return result('feature_off', COPY.featureOff, [A.WHATSAPP]);
  if (isNetworkError(err)) return result('network', COPY.network, [A.RETRY]);
  return result('unknown', COPY.unknown, [A.RETRY, A.WHATSAPP]);
}

/**
 * readmit_request_status: a terminal status that isn't `approved`, or a raised
 * error. Returns null for `pending` (keep waiting) and for `approved`.
 */
export function readmitStatusOutcome({ status, error } = {}) {
  if (error) {
    const code = errorCode(error);
    // P0410 = expired; 42501 = the server no longer knows this request under
    // this secret (it was cleaned up). Either way this request is over.
    if (code === 'P0410' || code === '42501') return result('expired', COPY.requestExpired, [A.ASK_AGAIN]);
    if (isNetworkError(error)) return result('network', COPY.network, [A.RETRY]);
    return result('unknown', COPY.unknown, [A.ASK_AGAIN, A.WHATSAPP]);
  }
  if (status === 'declined') return result('declined', COPY.declined, [A.ASK_AGAIN]);
  // `delivered` reaches a phone only after the 10-minute token window closed
  // without this phone collecting it — to this phone it is simply over.
  if (status === 'delivered' || status === 'expired') return result('expired', COPY.requestExpired, [A.ASK_AGAIN]);
  return null;
}

/** redeem_readmit_code: an ok:false body or a raised error. Null on success. */
export function redeemFailure({ data, error } = {}, { name } = {}) {
  if (error) {
    const code = errorCode(error);
    if (code === 'P0404') return result('no_such_player', COPY.noSuchPlayer(name), [A.EDIT_NAME]);
    if (code === 'P0410') return result('code_expired', COPY.codeExpired, [A.RETRY]);
    if (code === 'P0412') return result('still_active', messageOf(error) || COPY.gmStillActive(name), [A.WHATSAPP]);
    if (code === 'P0403') return result('ended', COPY.sessionEnded, []);
    if (code === 'P0501') return result('feature_off', COPY.featureOff, [A.WHATSAPP]);
    if (code === 'P0429') return result('locked', COPY.locked(60), [A.RETRY]);
    if (isNetworkError(error)) return result('network', COPY.network, [A.RETRY]);
    return result('unknown', COPY.unknown, [A.RETRY, A.WHATSAPP]);
  }
  if (data && data.ok === false) {
    if (data.reason === 'locked') {
      const s = Number(data.retry_after_s);
      return { ...result('locked', COPY.locked(s), [A.RETRY]), retryAfterS: Number.isFinite(s) && s > 0 ? Math.ceil(s) : 60 };
    }
    if (data.reason === 'wrong_code') return result('wrong_code', COPY.wrongCode, [A.RETRY]);
    return result('unknown', COPY.unknown, [A.RETRY, A.WHATSAPP]);
  }
  if (!data || !data.player_id || !data.claim_token) return result('unknown', COPY.unknown, [A.RETRY, A.WHATSAPP]);
  return null;
}

/** reactivate_my_seat raised. */
export function reactivateFailure(err) {
  const code = errorCode(err);
  if (code === 'P0412') return result('needs_approval', COPY.markedAway, [A.ASK_HOST, A.HOST_CODE]);
  if (code === 'P0403') return result('ended', COPY.sessionEnded, []);
  if (isNetworkError(err)) return result('network', COPY.network, [A.RETRY]);
  return result('unknown', COPY.unknown, [A.RETRY]);
}

/** GM side: approve_readmit returned ok:false or raised. Null on success. */
export function approveFailure({ data, error } = {}, { name } = {}) {
  if (error) {
    const code = errorCode(error);
    const msg = messageOf(error);
    if (code === 'P0412') {
      if (/still in the game/i.test(msg) || !msg) return result('still_active', COPY.gmStillActive(name), [A.MARK_AWAY]);
      return result('precondition', msg, []);
    }
    if (code === 'P0410') return result('gone', COPY.gmRequestGone, []);
    if (code === '42501') return result('not_gm', COPY.gmOnly, []);
    if (code === 'P0403') return result('not_started', COPY.gmNotStarted, []);
    if (code === 'P0501') return result('feature_off', COPY.featureOff, []);
    if (isNetworkError(error)) return result('network', COPY.network, [A.RETRY]);
    return result('unknown', COPY.unknown, [A.RETRY]);
  }
  if (data && data.ok === false) {
    if (data.reason === 'wrong_code') {
      return { ...result('wrong_code', COPY.gmWrongCode(data.tries_left), [A.RETYPE]), triesLeft: data.tries_left };
    }
    if (data.reason === 'too_many_wrong') return result('too_many_wrong', COPY.gmTooManyWrong(name), [A.GIVE_CODE]);
    if (data.reason === 'name_taken') return result('name_taken', COPY.gmNameTaken, []);
    return result('unknown', COPY.unknown, [A.RETRY]);
  }
  if (!data || data.ok !== true) return result('unknown', COPY.unknown, [A.RETRY]);
  return null;
}

/** GM side: decline_readmit / issue_readmit_code / set_seat_active raised. */
export function gmActionFailure(err, { name } = {}) {
  const code = errorCode(err);
  const msg = messageOf(err);
  if (code === 'P0412') {
    if (/still in the game/i.test(msg) || !msg) return result('still_active', COPY.gmStillActive(name), [A.MARK_AWAY]);
    return result('precondition', msg, []);
  }
  if (code === 'P0410') return result('gone', COPY.gmRequestGone, []);
  if (code === '42501') return result('not_gm', COPY.gmOnly, []);
  if (code === 'P0403') return result('not_started', COPY.gmNotStarted, []);
  if (code === 'P0404') return result('no_such_player', COPY.noSuchPlayer(name), []);
  if (code === 'P0501') return result('feature_off', COPY.featureOff, []);
  if (isNetworkError(err)) return result('network', COPY.network, [A.RETRY]);
  return result('unknown', COPY.unknown, [A.RETRY]);
}
